// Orchestrates one participant's journey through a video session:
// lobby (preview + device choice) → waiting/admission → in-call → ended.
// All browser APIs are touched inside effects/handlers only, so the page
// that uses this hook is safe to server-render.
//
// Two streams are kept apart: the *camera* stream (raw getUserMedia) and
// the *outgoing* stream (what the preview shows and the call sends). They
// are the same object unless software background blur is on, in which case
// the outgoing video track is the processed canvas capture.
import { useCallback, useEffect, useRef, useState } from "react"
import {
  loadBackgroundPreference,
  saveBackgroundPreference,
  sceneFor,
  setNativeBlur,
  SoftwareBlur,
  type BackgroundMode,
  type BackgroundStatus,
} from "./background"
import { Call, type CallDiagnostics } from "./rtc"
import { currentDeviceId, getMedia, listDevices, stopStream, type DeviceLists } from "./devices"
import { defaultSignalingUrl, Signaling } from "./signaling"
import type { IceConfig, PresentationKind, Role, ServerMessage, SocketStatus } from "./types"

export type Stage = "lobby" | "waiting" | "call" | "ended"
export type ConnectionState = RTCPeerConnectionState | "idle"

export type VideoSessionState = {
  role: Role | null
  stage: Stage
  banner: string
  mediaError: boolean
  mediaReady: boolean
  // getUserMedia has neither resolved nor rejected for a while: no prompt
  // appeared, which on a phone usually means the browser app itself lacks
  // the camera permission and so cannot even ask.
  mediaStalled: boolean
  devices: DeviceLists
  camId: string
  micId: string
  knock: string | null
  endedReason: string
  micOn: boolean
  camOn: boolean
  socket: SocketStatus
  connection: ConnectionState
  slow: boolean
  devIce: boolean
  background: BackgroundMode
  backgroundStatus: BackgroundStatus
  canShare: boolean
  sharing: boolean
  whiteboard: boolean
  localStream: MediaStream | null
  localScreenStream: MediaStream | null
  remoteStream: MediaStream | null
  remoteScreenStream: MediaStream | null
  remoteScreenKind: PresentationKind
  diagnostics: CallDiagnostics | null
}

const INITIAL: VideoSessionState = {
  role: null,
  stage: "lobby",
  banner: "",
  mediaError: false,
  mediaReady: false,
  mediaStalled: false,
  devices: { cams: [], mics: [] },
  camId: "",
  micId: "",
  knock: null,
  endedReason: "",
  micOn: true,
  camOn: true,
  socket: "closed",
  connection: "idle",
  slow: false,
  devIce: false,
  background: "none",
  backgroundStatus: "off",
  canShare: false,
  sharing: false,
  whiteboard: false,
  localStream: null,
  localScreenStream: null,
  remoteStream: null,
  remoteScreenStream: null,
  remoteScreenKind: "screen",
  diagnostics: null,
}

const SLOW_CONNECT_MS = 15000
// A peer connection that has gathered nothing at all after this long is
// stuck, not slow: seen on Samsung Internet when the connection is created
// the instant camera permission lands, before the WebRTC stack has its
// network layer up. An ICE restart is what a manual rejoin did by hand.
const EMPTY_GATHER_MS = 6000
// How long a getUserMedia call may sit unanswered before the lobby says so.
const MEDIA_STALL_MS = 8000

type Options = {
  roomId: string
  token: string
  signalingUrl: string | null
}

export function useVideoSession({ roomId, token, signalingUrl }: Options) {
  const [state, setState] = useState<VideoSessionState>(INITIAL)
  const patch = useCallback((p: Partial<VideoSessionState>) => setState((s) => ({ ...s, ...p })), [])
  // The latest state, for callbacks that must read it without re-creating
  // themselves on every change (cancel needs the stage).
  const stateRef = useRef(state)
  stateRef.current = state

  const cameraRef = useRef<MediaStream | null>(null) // raw camera + mic
  const outgoingVideoRef = useRef<MediaStreamTrack | null>(null) // what we send/preview
  const streamRef = useRef<MediaStream | null>(null) // outgoing stream object
  const blurRef = useRef<SoftwareBlur | null>(null)
  const nativeBlurRef = useRef(false)
  const signalingRef = useRef<Signaling | null>(null)
  const callRef = useRef<Call | null>(null)
  const roleRef = useRef<Role>("guest")
  const hostKeyRef = useRef<string | null>(null)
  const pairedRef = useRef(false)
  const slowTimerRef = useRef<number | null>(null)
  const gatherTimerRef = useRef<number | null>(null)
  const gatherRestartsRef = useRef(0)
  const camOnRef = useRef(true)
  const screenRef = useRef<MediaStream | null>(null) // active screen capture, if sharing
  const whiteboardRef = useRef<MediaStream | null>(null) // whiteboard canvas capture, if open
  const whiteboardPulseRef = useRef<number | null>(null) // the board's frame keep-alive
  const remoteStreamsRef = useRef(new Map<string, MediaStream>()) // all streams the peer sends
  const remoteScreenIdRef = useRef<string | null>(null) // which of them is their presentation
  const remoteScreenKindRef = useRef<PresentationKind>("screen")

  const cameraTrack = () => cameraRef.current?.getVideoTracks()[0] ?? null

  // Rebuild the outgoing stream from the current outgoing video + camera audio.
  const refreshOutgoing = useCallback(
    (videoTrack: MediaStreamTrack | null) => {
      outgoingVideoRef.current = videoTrack
      const tracks: MediaStreamTrack[] = []
      if (videoTrack) tracks.push(videoTrack)
      const audio = cameraRef.current?.getAudioTracks()[0]
      if (audio) tracks.push(audio)
      const next = new MediaStream(tracks)
      streamRef.current = next
      if (videoTrack) videoTrack.enabled = camOnRef.current
      callRef.current?.replaceTrack("video", videoTrack).catch(() => {})
      patch({ localStream: next })
    },
    [patch],
  )

  const stopWhiteboardPulse = () => {
    if (whiteboardPulseRef.current !== null) window.clearInterval(whiteboardPulseRef.current)
    whiteboardPulseRef.current = null
  }

  const clearSlowTimer = () => {
    if (slowTimerRef.current !== null) window.clearTimeout(slowTimerRef.current)
    slowTimerRef.current = null
  }

  const teardown = useCallback(
    (endedReason: string) => {
      pairedRef.current = false
      clearSlowTimer()
      if (gatherTimerRef.current !== null) window.clearTimeout(gatherTimerRef.current)
      gatherTimerRef.current = null
      gatherRestartsRef.current = 0
      callRef.current?.close()
      callRef.current = null
      blurRef.current?.stop()
      blurRef.current = null
      stopStream(screenRef.current)
      screenRef.current = null
      stopWhiteboardPulse()
      stopStream(whiteboardRef.current)
      whiteboardRef.current = null
      stopStream(cameraRef.current)
      cameraRef.current = null
      outgoingVideoRef.current?.stop()
      outgoingVideoRef.current = null
      streamRef.current = null
      signalingRef.current?.close()
      signalingRef.current = null
      remoteStreamsRef.current.clear()
      remoteScreenIdRef.current = null
      patch({
        stage: "ended",
        endedReason,
        banner: "",
        knock: null,
        connection: "idle",
        slow: false,
        sharing: false,
        whiteboard: false,
        localStream: null,
        localScreenStream: null,
        remoteStream: null,
        remoteScreenStream: null,
      })
    },
    [patch],
  )

  // The peer can send up to two streams at once: their camera and (while
  // they present) their screen or whiteboard. Which is which comes from the
  // screenShare signal's streamId — ordering-safe either way round, since
  // this runs again when the flag or a new track arrives.
  const applyRemoteStreams = useCallback(() => {
    const screenId = remoteScreenIdRef.current
    let camera: MediaStream | null = null
    let screen: MediaStream | null = null
    for (const stream of remoteStreamsRef.current.values()) {
      if (stream.id === screenId) screen = stream
      else camera = stream
    }
    patch({ remoteStream: camera, remoteScreenStream: screen, remoteScreenKind: remoteScreenKindRef.current })
  }, [patch])

  // Restart ICE if nothing has been gathered by the deadline, up to twice.
  const armGatherWatchdog = useCallback(() => {
    if (gatherTimerRef.current !== null) window.clearTimeout(gatherTimerRef.current)
    gatherTimerRef.current = window.setTimeout(() => {
      gatherTimerRef.current = null
      const d = stateRef.current.diagnostics
      if (!callRef.current || (d && d.local.length > 0) || gatherRestartsRef.current >= 2) return
      gatherRestartsRef.current += 1
      callRef.current.restart()
      armGatherWatchdog()
    }, EMPTY_GATHER_MS)
  }, [])

  const beginCall = useCallback(
    (ice: IceConfig | undefined) => {
      if (callRef.current || !streamRef.current || !signalingRef.current) return
      const call = new Call({
        signaling: signalingRef.current,
        polite: roleRef.current === "guest",
        stream: streamRef.current,
        iceServers: ice?.iceServers ?? [],
        onTrack: (stream) => {
          remoteStreamsRef.current.set(stream.id, stream)
          applyRemoteStreams()
        },
        onConnectionState: (connection) => {
          if (connection === "connected") {
            clearSlowTimer()
            patch({ connection, slow: false })
          } else {
            patch({ connection })
          }
        },
        onDiagnostics: (diagnostics) => {
          patch({ diagnostics })
          // Something gathered: stand the empty-gather watchdog down.
          if (diagnostics.local.length > 0 && gatherTimerRef.current !== null) {
            window.clearTimeout(gatherTimerRef.current)
            gatherTimerRef.current = null
          }
        },
      })
      callRef.current = call
      call.start()
      armGatherWatchdog()
      pairedRef.current = true
      clearSlowTimer()
      slowTimerRef.current = window.setTimeout(() => patch({ slow: true }), SLOW_CONNECT_MS)
      patch({ stage: "call", connection: "new", slow: false, devIce: !!ice?.dev, knock: null })
    },
    [applyRemoteStreams, armGatherWatchdog, patch],
  )

  const handleMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.t) {
        case "ready":
          if (roleRef.current === "host") patch({ stage: "waiting" })
          if (msg.paired) beginCall(msg.ice)
          break
        case "waiting":
          patch({ stage: "waiting" })
          break
        case "knock":
          patch({ knock: msg.peerId })
          break
        case "admitted":
          break // 'paired' follows and starts the call
        case "paired":
          beginCall(msg.ice)
          break
        case "declined":
          teardown("The session is not available right now. Please contact your therapist.")
          break
        case "busy":
          teardown("This session already has a participant.")
          break
        case "signal":
          if (msg.data.screenShare !== undefined) {
            if (msg.data.screenShare) {
              remoteScreenIdRef.current = msg.data.streamId ?? null
              remoteScreenKindRef.current = msg.data.kind ?? "screen"
            } else {
              if (remoteScreenIdRef.current) remoteStreamsRef.current.delete(remoteScreenIdRef.current)
              remoteScreenIdRef.current = null
              remoteScreenKindRef.current = "screen"
            }
            applyRemoteStreams()
            break
          }
          callRef.current?.handleSignal(msg.data)
          break
        case "peer-reconnected": {
          patch({ banner: "" })
          callRef.current?.restart()
          // A reloaded peer has lost app-level state — restate which of our
          // streams is the presentation so they render it un-cropped.
          const presentation = screenRef.current
            ? { stream: screenRef.current, kind: "screen" as const }
            : whiteboardRef.current
              ? { stream: whiteboardRef.current, kind: "whiteboard" as const }
              : null
          if (presentation) {
            signalingRef.current?.send({
              t: "signal",
              data: { screenShare: true, streamId: presentation.stream.id, kind: presentation.kind },
            })
          }
          break
        }
        case "peer-left":
          if (msg.final) teardown("The other participant has left the session.")
          else patch({ banner: "The other participant lost connection — waiting for them to return…" })
          break
        case "room-closed":
          teardown(msg.reason === "expired" ? "This session link has expired." : "This session has ended.")
          break
        case "error":
          teardown(
            msg.error === "expired"
              ? "This session link has expired."
              : msg.error === "no-room"
                ? "This session link is no longer valid."
                : msg.error === "bad-key"
                  ? "This host link is not valid."
                  : "Could not join this session.",
          )
          break
      }
    },
    [applyRemoteStreams, beginCall, patch, teardown],
  )

  // ---- background blur --------------------------------------------------------

  const applyBackground = useCallback(
    async (mode: BackgroundMode) => {
      const cam = cameraTrack()
      if (!cam) return
      saveBackgroundPreference(mode)

      // Always undo the previous tier first.
      if (blurRef.current) {
        blurRef.current.stop()
        blurRef.current = null
      }
      if (nativeBlurRef.current) {
        await setNativeBlur(cam, false)
        nativeBlurRef.current = false
      }

      if (mode === "none") {
        patch({ background: "none", backgroundStatus: "off" })
        refreshOutgoing(cam)
        return
      }

      patch({ background: mode, backgroundStatus: "loading" })
      refreshOutgoing(cam)

      // A scene needs the person cut out, which only the software tier does;
      // native blur can only blur.
      const scene = sceneFor(mode)
      if (!scene && (await setNativeBlur(cam, true))) {
        nativeBlurRef.current = true
        patch({ backgroundStatus: "native" })
        return
      }

      const blur = new SoftwareBlur(() => {
        // Device can't keep up: fall back to the plain camera and say so.
        blurRef.current = null
        saveBackgroundPreference("none")
        patch({ background: "none", backgroundStatus: "too-slow" })
        const current = cameraTrack()
        if (current) refreshOutgoing(current)
      })
      blurRef.current = blur
      try {
        await blur.setScene(scene)
        const processed = await blur.start(cam)
        if (blurRef.current !== blur) {
          blur.stop() // superseded while loading
          return
        }
        patch({ backgroundStatus: "software" })
        refreshOutgoing(processed)
      } catch (err) {
        console.error("background blur", err)
        blur.stop()
        if (blurRef.current === blur) blurRef.current = null
        saveBackgroundPreference("none")
        patch({ background: "none", backgroundStatus: "unsupported" })
      }
    },
    [patch, refreshOutgoing],
  )

  // ---- presenting: screen share & whiteboard ----------------------------------
  // A presentation (a screen capture, or the whiteboard canvas captured with
  // canvas.captureStream) goes out as a SECOND video track next to the
  // camera, so the other side keeps seeing the presenter's face. Adding or
  // removing the track renegotiates via the perfect-negotiation handlers in
  // rtc.ts; the screenShare signal tells the peer which incoming stream is
  // the presentation and what to call it. Screen share and whiteboard are
  // mutually exclusive — starting one stops the other.

  const stopScreenShare = useCallback(() => {
    const screen = screenRef.current
    if (!screen) return
    screenRef.current = null
    callRef.current?.removeScreenTrack()
    stopStream(screen)
    signalingRef.current?.send({ t: "signal", data: { screenShare: false } })
    patch({ sharing: false, localScreenStream: null })
  }, [patch])

  // Closes the whiteboard: stop sending it, and unmount the panel (which
  // owns the canvas). Whiteboard strokes only ever exist in that canvas —
  // closing discards them, and nothing was persisted anywhere.
  const closeWhiteboard = useCallback(() => {
    if (whiteboardRef.current) {
      callRef.current?.removeScreenTrack()
      stopWhiteboardPulse()
      stopStream(whiteboardRef.current)
      whiteboardRef.current = null
      signalingRef.current?.send({ t: "signal", data: { screenShare: false } })
    }
    patch({ whiteboard: false })
  }, [patch])

  const startScreenShare = useCallback(async () => {
    if (screenRef.current || !callRef.current) return
    let screen: MediaStream
    try {
      screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
    } catch {
      return // picker dismissed or permission refused — not an error
    }
    const track = screen.getVideoTracks()[0]
    if (!track || !callRef.current) {
      stopStream(screen)
      return
    }
    closeWhiteboard()
    // Prefer resolution over frame rate so shared text stays readable.
    try {
      track.contentHint = "detail"
    } catch {
      /* older browsers */
    }
    screenRef.current = screen
    track.onended = () => stopScreenShare() // the browser's "Stop sharing" bar
    callRef.current.addScreenTrack(track, screen)
    signalingRef.current?.send({ t: "signal", data: { screenShare: true, streamId: screen.id, kind: "screen" } })
    patch({ sharing: true, localScreenStream: screen })
  }, [closeWhiteboard, patch, stopScreenShare])

  // Shows the whiteboard panel; the canvas arrives via attachWhiteboardCanvas
  // once the panel has mounted.
  const openWhiteboard = useCallback(() => {
    if (!callRef.current) return
    stopScreenShare()
    patch({ whiteboard: true })
  }, [patch, stopScreenShare])

  const attachWhiteboardCanvas = useCallback((canvas: HTMLCanvasElement) => {
    if (!callRef.current || whiteboardRef.current) return
    const stream = canvas.captureStream(15)
    const track = stream.getVideoTracks()[0]
    if (!track) return
    // Line work wants resolution, not frame rate.
    try {
      track.contentHint = "detail"
    } catch {
      /* older browsers */
    }
    whiteboardRef.current = stream
    // A canvas capture only produces a frame when the canvas is drawn to, so
    // a board that has sat still and then changes once — a diagram dropped
    // in, or Clear — hands the encoder a single frame after a long silence,
    // and under a weak link the encoder holds or drops lone frames (measured:
    // an all-white Clear took 5–6s to arrive, diagrams 2–4s, and some frames
    // never did). Asking for a frame ten times a second keeps the stream
    // continuous: an unchanged frame encodes to almost nothing, and a change
    // is followed by another frame 100ms later rather than by nothing.
    stopWhiteboardPulse()
    const requestFrame = (track as MediaStreamTrack & { requestFrame?: () => void }).requestFrame
    if (requestFrame) whiteboardPulseRef.current = window.setInterval(() => requestFrame.call(track), 100)
    callRef.current.addScreenTrack(track, stream)
    signalingRef.current?.send({ t: "signal", data: { screenShare: true, streamId: stream.id, kind: "whiteboard" } })
  }, [])

  // ---- mount: role, media, listeners ------------------------------------------

  // Role comes from the URL fragment (#k=<hostKey>), which the browser never
  // sends to the server. Then acquire camera + mic for the lobby preview.
  useEffect(() => {
    const key = new URLSearchParams(window.location.hash.slice(1)).get("k")
    hostKeyRef.current = key
    roleRef.current = key ? "host" : "guest"
    patch({
      role: roleRef.current,
      canShare: typeof navigator.mediaDevices?.getDisplayMedia === "function",
    })

    let cancelled = false
    const stallTimer = window.setTimeout(() => {
      if (!cancelled) patch({ mediaStalled: true })
    }, MEDIA_STALL_MS)
    ;(async () => {
      try {
        const stream = await getMedia()
        window.clearTimeout(stallTimer)
        if (cancelled) {
          stopStream(stream)
          return
        }
        cameraRef.current = stream
        const devices = await listDevices()
        patch({
          mediaReady: true,
          mediaStalled: false,
          devices,
          camId: currentDeviceId(stream, "video"),
          micId: currentDeviceId(stream, "audio"),
        })
        refreshOutgoing(stream.getVideoTracks()[0] ?? null)
        if (loadBackgroundPreference() === "blur") applyBackground("blur")
      } catch (err) {
        window.clearTimeout(stallTimer)
        console.error(err)
        if (!cancelled) patch({ mediaError: true, mediaStalled: false })
      }
    })()

    const onUnload = () => {
      if (pairedRef.current) signalingRef.current?.send({ t: "bye" })
    }
    // Software blur runs at ~1 fps in a background tab (timer throttling), so
    // send the plain camera while hidden and switch back when visible. This
    // only touches the camera sender, so it is safe (and useful) mid-share:
    // the tab is naturally hidden while presenting another window.
    const onVisibility = () => {
      if (!callRef.current || !blurRef.current) return
      const track = document.hidden ? cameraTrack() : outgoingVideoRef.current
      if (track) callRef.current.replaceTrack("video", track).catch(() => {})
    }
    window.addEventListener("beforeunload", onUnload)
    document.addEventListener("visibilitychange", onVisibility)

    return () => {
      cancelled = true
      window.removeEventListener("beforeunload", onUnload)
      document.removeEventListener("visibilitychange", onVisibility)
      clearSlowTimer()
      callRef.current?.close()
      blurRef.current?.stop()
      stopStream(screenRef.current)
      stopWhiteboardPulse()
      stopStream(whiteboardRef.current)
      stopStream(cameraRef.current)
      outgoingVideoRef.current?.stop()
      signalingRef.current?.close()
    }
  }, [applyBackground, patch, refreshOutgoing])

  // ---- actions ----------------------------------------------------------------

  const join = useCallback(() => {
    if (signalingRef.current) return
    const signaling = new Signaling({
      url: signalingUrl ?? defaultSignalingUrl(),
      token,
      roomId,
      role: roleRef.current,
      hostKey: hostKeyRef.current,
      onMessage: handleMessage,
      onStatus: (socket) =>
        patch({ socket, banner: socket === "reconnecting" ? "Connection lost — reconnecting…" : "" }),
      // We were offline: whatever the other side offered meanwhile was lost
      // to them too. Restart ICE from this end so a fresh offer goes out on a
      // socket that is up — the peer-reconnected the server sends *them*
      // covers the other direction.
      onReconnected: () => {
        if (pairedRef.current) callRef.current?.restart()
      },
    })
    signalingRef.current = signaling
    signaling.connect()
  }, [handleMessage, patch, roomId, signalingUrl, token])

  const admit = useCallback(() => {
    if (state.knock) signalingRef.current?.send({ t: "admit", peerId: state.knock })
    patch({ knock: null })
  }, [patch, state.knock])

  const decline = useCallback(() => {
    if (state.knock) signalingRef.current?.send({ t: "decline", peerId: state.knock })
    patch({ knock: null })
  }, [patch, state.knock])

  const toggleMic = useCallback(() => {
    const track = cameraRef.current?.getAudioTracks()[0]
    if (!track) return
    track.enabled = !track.enabled
    patch({ micOn: track.enabled })
  }, [patch])

  const toggleCam = useCallback(() => {
    const cam = cameraTrack()
    if (!cam) return
    camOnRef.current = !camOnRef.current
    cam.enabled = camOnRef.current
    if (outgoingVideoRef.current && outgoingVideoRef.current !== cam) {
      outgoingVideoRef.current.enabled = camOnRef.current
    }
    patch({ camOn: camOnRef.current })
  }, [patch])

  const switchDevice = useCallback(
    async (kind: "audio" | "video", deviceId: string) => {
      const camera = cameraRef.current
      if (!camera || !deviceId) return
      const fresh = await getMedia(
        kind === "video"
          ? { video: { deviceId: { exact: deviceId } }, audio: false }
          : { audio: { deviceId: { exact: deviceId } }, video: false },
      )
      const newTrack = kind === "video" ? fresh.getVideoTracks()[0] : fresh.getAudioTracks()[0]
      const oldTrack = kind === "video" ? camera.getVideoTracks()[0] : camera.getAudioTracks()[0]
      if (!newTrack) return
      if (oldTrack) {
        newTrack.enabled = oldTrack.enabled
        camera.removeTrack(oldTrack)
        oldTrack.stop()
      }
      camera.addTrack(newTrack)
      for (const t of fresh.getTracks()) if (t !== newTrack) t.stop()

      if (kind === "audio") {
        await callRef.current?.replaceTrack("audio", newTrack)
        refreshOutgoing(outgoingVideoRef.current)
        patch({ micId: deviceId })
        return
      }

      patch({ camId: deviceId })
      if (blurRef.current) {
        await blurRef.current.setSource(newTrack) // output track unchanged
        refreshOutgoing(outgoingVideoRef.current)
      } else if (nativeBlurRef.current) {
        nativeBlurRef.current = await setNativeBlur(newTrack, true)
        if (!nativeBlurRef.current) patch({ background: "none", backgroundStatus: "unsupported" })
        refreshOutgoing(newTrack)
      } else {
        refreshOutgoing(newTrack)
      }
    },
    [patch, refreshOutgoing],
  )

  const setBackground = useCallback(
    (mode: BackgroundMode) => {
      applyBackground(mode).catch((err) => console.error(err))
    },
    [applyBackground],
  )

  const retry = useCallback(() => {
    callRef.current?.restart()
    patch({ slow: false })
    clearSlowTimer()
    slowTimerRef.current = window.setTimeout(() => patch({ slow: true }), SLOW_CONNECT_MS)
  }, [patch])

  const end = useCallback(() => {
    signalingRef.current?.send({ t: "bye" })
    teardown("Session ended.")
  }, [teardown])

  // Leaving before the call: from the lobby (nothing announced yet) or from
  // the waiting room (the host is known to the relay, and a guest may be
  // knocking — a "bye" lets them see "the other participant has left" rather
  // than wait on a host who is gone). Releases the camera and microphone and
  // closes the socket. The link stays valid — the same room can be opened
  // again — so this is a way out of the tab, not a way to void the session.
  const cancel = useCallback(() => {
    if (stateRef.current.stage === "waiting") signalingRef.current?.send({ t: "bye" })
    teardown("You left before the session started.")
  }, [teardown])

  return {
    state,
    join,
    admit,
    decline,
    toggleMic,
    toggleCam,
    switchDevice,
    setBackground,
    startScreenShare,
    stopScreenShare,
    openWhiteboard,
    closeWhiteboard,
    attachWhiteboardCanvas,
    retry,
    end,
    cancel,
  }
}
